import React, {
  Fragment,
  useState,
  MutableRefObject,
  PropsWithChildren,
  ReactText,
  UIEventHandler,
  useEffect,
  forwardRef,
  useImperativeHandle,
  ForwardedRef,
  SetStateAction,
  Dispatch,
} from 'react';
import cx from 'classnames';
import {
  Box,
  CircularProgress,
  Table as MuiTable,
  TableBody,
  TableCell,
  TableRow,
  Collapse,
  TableFooter,
} from '@mui/material';
import {
  Column as ReactTableColumn,
  Row as ReactTableRow,
  useTable,
  useSortBy,
  TableState,
  SortingRule,
  useRowSelect,
  useResizeColumns,
  useFlexLayout,
  MetaBase,
} from 'react-table';
import { SxProps, Theme, styled } from '@mui/material/styles';
import TableHeader from './TableHeader';
import useToggle from 'new/hooks/useToggle';
import IconFontAwesome from 'new/components/IconFontAwesome';
import Checkbox from 'new/components/Checkbox';

export type TableRefType =
  | { toggleAllRowsSelected: (value: boolean) => void }
  | undefined;

export type Column<D extends object> = ReactTableColumn<D>;

type TableProps<D extends object> = {
  data: D[];
  columns: Column<D>[];
  height?: ReactText;
  onRowClick?: (row: D) => void;
  onRowDoubleClick?: (row: D) => void;
  sortBy?: Array<SortingRule<D>>;
  saveSortBy?: Array<SortingRule<D>>;
  isLoading?: boolean;
  className?: string;
  emptyTableInfo?: string;
  containerRef?: MutableRefObject<HTMLDivElement | undefined>;
  onContainerScroll?: UIEventHandler<HTMLDivElement>;
  disableRowHover?: boolean;
  onRowHover?: (row?: D) => void;
  onRowsSelect?: (rows: ReactTableRow<D>[]) => void;
  useControlledState?: (
    state: TableState<D>,
    meta: MetaBase<D>,
  ) => TableState<D>;
  withCustomSelect?: boolean;
  isTableLazyLoad?: boolean;
  setEndOfResults?: Dispatch<SetStateAction<boolean>>;
  headerNotCaps?: boolean;
  wrapperSx?: SxProps<Theme>;
  getRowClass?: (row: D) => Record<string, boolean>;
};

const LoadingOverlay = styled(Box)({
  position: 'absolute',
  zIndex: 5,
  height: '100%',
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  background: 'rgba(255, 255, 255, 0.6)',
});

const StyledTable = styled(MuiTable, {
  shouldForwardProp: (prop) => prop !== 'disableRowHover',
})<{ disableRowHover?: boolean; headerNotCaps?: boolean }>(
  ({ theme, disableRowHover, headerNotCaps }) => {
    const darkGrey = '#EEF0F3';
    const lightGrey = '#FEFEFE';
    const darkBlue = '#C0E2F1';
    const blueBackground = `${theme.palette.primary.light} !important`;
    const borderBlueBackground = `1px solid ${theme.palette.primary.light}`;
    const blueBorder = `1px solid ${theme.palette.primary.main}`;

    const collapseRowHover = (grey: '#EEF0F3' | '#FEFEFE') => ({
      ':hover': {
        background: blueBackground,

        '+ tr.MuiTableRow-root': {
          background: blueBackground,
        },
      },

      '&.open': {
        '&.isHover': {
          background: blueBackground,
          border: blueBorder,
          borderBottom: borderBlueBackground,
        },

        ':hover': {
          borderBottom: borderBlueBackground,

          '+ tr.MuiTableRow-root': {
            border: blueBorder,
            borderTop: borderBlueBackground,
          },
        },
      },

      '+ tr.MuiTableRow-root': {
        background: `${grey}`,

        ':hover': {
          borderTop: borderBlueBackground,
          background: blueBackground,
        },
      },
    });

    return {
      '.MuiTableHead-root': {
        top: 0,
        position: 'sticky',
        zIndex: 2,
      },

      '.MuiTableRow-head': {
        border: 'none !important',
      },

      '.MuiTableCell-head': {
        background: '#fff',
        textTransform: headerNotCaps ? '' : 'uppercase',
        fontWeight: 400,
        marginRight: -10,
        paddingRight: 0,
        marginLeft: 8,
        paddingLeft: theme.spacing(0.5),

        '&:first-of-type': {
          marginLeft: 0,
          paddingLeft: theme.spacing(2),
          paddingRight: 20,
        },

        '&:last-of-type': {
          marginRight: 0,
        },

        '&.MuiTableCell-alignRight > div': {
          justifyContent: 'flex-end',
        },

        '&:hover': {
          '.MuiTableSortLabel-root:not(.Mui-active)': {
            color: 'rgba(0, 0, 0, 0.6)',

            svg: {
              opacity: 0.5,
            },
          },
        },

        '> div': {
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'flex-start',

          '.MuiTableSortLabel-root': {
            position: 'relative',
            top: 4,
            left: 0,
            right: 0,

            svg: {
              marginLeft: 0,
            },
          },
        },
        '.MuiCheckbox-root': {
          padding: 0,
        },
      },

      'tbody > .MuiTableRow-root': {
        background: darkGrey,
        boxSizing: 'content-box',
        border: '1px solid transparent',
        borderColor: 'transparent',
        overflow: 'hidden',

        '&.isSelected': {
          background: `${darkBlue} !important`,
          fontWeight: 'bold',
        },

        '.rowPointer': {
          cursor: 'pointer',
        },

        '.MuiTableCell-root': {
          border: 'none',
          padding: theme.spacing(0.5),
          alignSelf: 'center',

          '&:first-of-type': {
            paddingLeft: theme.spacing(1.5),
          },

          '.MuiCheckbox-root': {
            padding: 0,
          },
        },

        '&:nth-of-type(even)': {
          background: theme.palette.grey[50],
        },

        ':hover': {
          ...(!disableRowHover
            ? {
                backgroundColor: 'rgba(0, 161, 223, 0.1) !important',
                border: blueBorder,
              }
            : {}),
          '.visibleOnHover': {
            display: 'initial',
          },
        },
      },

      '&.isOnRowClick tr': {
        cursor: 'pointer',
      },

      '&.hasCollapseData tbody': {
        '.cellWithArrowForCollapse': {
          svg: {
            cursor: 'pointer',
          },

          '&.open svg': {
            transform: 'rotate(180deg)',
            transition: '.2s',
          },
        },

        '.collapseRow': {
          overflow: 'visible',

          '&:not(.open)': {
            border: 'none',
          },
        },

        '.rowDarkGrey': {
          ...collapseRowHover('#EEF0F3'),
        },

        '.rowLightGrey': {
          background: `${lightGrey} !important`,
          ...collapseRowHover('#FEFEFE'),
        },
      },

      '.collapseCell': {
        padding: 0,
      },

      '.emptyTableInfo': {
        background: '#fff',
        textAlign: 'center',

        td: {
          borderBottom: 'unset',
        },
      },
    };
  },
);

type RowProps<D extends object> = {
  row: ReactTableRow<D>;
  index: number;
  hasCollapseData: boolean;
  onRowClick?: (row: D) => void;
  onRowDoubleClick?: (row: D) => void;
  onRowHover?: (row?: D) => void;
  selectedFlatRows?: boolean;
  withRowSelect?: boolean;
  withCustomSelect?: boolean;
  getRowClass?: (row: D) => Record<string, boolean>;
};

const Row = <D extends object>({
  row,
  index,
  hasCollapseData,
  onRowClick,
  onRowDoubleClick,
  onRowHover,
  withRowSelect,
  withCustomSelect,
  getRowClass,
}: RowProps<D>) => {
  const [open, toggleOpen] = useToggle();
  const [isHover, setIsHover] = useState(false);

  const indexOfCollapseColumn = row.cells.findIndex(
    (cell) => cell.column.id === 'collapseData',
  );
  const collapsedRow = row.cells.find(
    (cell) => cell.column.id === 'collapseData',
  );

  return (
    <Fragment key={row.id}>
      <TableRow
        className={cx(`row${index % 2 === 0 ? 'DarkGrey' : 'LightGrey'}`, {
          isHover,
          open,
          isSelected: row.isSelected,
          ...getRowClass?.(row.original),
        })}
        {...row.getRowProps()}
        onClick={() => onRowClick?.(row.original)}
        onDoubleClick={() => onRowDoubleClick?.(row.original)}
        onMouseEnter={() => {
          setIsHover(true);
          return onRowHover?.(row.original);
        }}
        onMouseLeave={() => {
          setIsHover(false);
          return onRowHover?.(undefined);
        }}
      >
        {withRowSelect && !withCustomSelect && (
          <TableCell>
            <Checkbox
              checked={row.isSelected}
              onClick={(e) => {
                e.stopPropagation();
              }}
              onChange={(e) => {
                e.stopPropagation();
                row.toggleRowSelected();
              }}
            />
          </TableCell>
        )}
        {row.cells.map((cell, index) => {
          if (hasCollapseData && index === indexOfCollapseColumn) {
            return (
              <TableCell
                className={cx('cellWithArrowForCollapse', { open })}
                {...cell.getCellProps()}
                align={cell.column.align}
              >
                <IconFontAwesome name="faAngleDown" onClick={toggleOpen} />
              </TableCell>
            );
          }

          return (
            <TableCell {...cell.getCellProps()} align={cell.column.align}>
              {cell.render('Cell', { isHovered: isHover })}
            </TableCell>
          );
        })}
      </TableRow>
      {hasCollapseData && (
        <TableRow
          onMouseEnter={() => setIsHover(true)}
          onMouseLeave={() => setIsHover(false)}
          className={cx('collapseRow', { open })}
          {...row.getRowProps()}
          key={row.getRowProps().key + 'hidden'}
        >
          <TableCell
            className={cx('collapseCell', { open })}
            {...collapsedRow?.getCellProps()}
          >
            <Collapse in={open}>{collapsedRow?.render('Cell')}</Collapse>
          </TableCell>
        </TableRow>
      )}
    </Fragment>
  );
};

const scrollParameters = {
  heightAdjustment: 60,
  loadRecords: 20,
  initialRecords: 20,
};

function TableInner<D extends object>(
  {
    data = [],
    columns,
    onRowClick,
    onRowDoubleClick,
    isLoading,
    sortBy,
    saveSortBy,
    className,
    emptyTableInfo,
    height = 'auto',
    containerRef,
    onContainerScroll,
    disableRowHover = false,
    children,
    onRowHover,
    onRowsSelect,
    useControlledState,
    withCustomSelect,
    isTableLazyLoad,
    setEndOfResults,
    headerNotCaps,
    wrapperSx,
    getRowClass,
  }: PropsWithChildren<TableProps<D>>,
  ref: ForwardedRef<TableRefType>,
) {
  const [currentTopIndex, setCurrentTopIndex] = useState(
    Math.ceil(window.innerHeight / scrollParameters.heightAdjustment),
  );
  const initialState = {
    ...(sortBy && { sortBy }),
  } as Partial<TableState<D>>;
  const defaultColumn = React.useMemo(
    () => ({
      minWidth: 10,
      width: 150,
      maxWidth: 200,
    }),
    [],
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    isAllRowsSelected,
    toggleAllRowsSelected,
    selectedFlatRows,
  } = useTable(
    {
      data,
      columns,
      defaultColumn,
      initialState,
      useControlledState,
      autoResetHiddenColumns: false,
    },
    useSortBy,
    useRowSelect,
    useResizeColumns,
    useFlexLayout,
  );

  const hasCollapseData = columns.some((col) => col.id === 'collapseData');
  const isOnRowClick = !!onRowClick || !!onRowDoubleClick;

  useEffect(() => {
    if (data.length) {
      onRowsSelect?.(selectedFlatRows);
    }
  }, [selectedFlatRows]);

  useImperativeHandle(ref, () => ({
    toggleAllRowsSelected,
  }));

  const onContainerScrollTable:
    | React.UIEventHandler<HTMLDivElement>
    | undefined = (e) => {
    const { scrollTop, scrollHeight, offsetHeight } =
      e.target as HTMLDivElement;
    if (scrollTop + offsetHeight + 5 >= scrollHeight) {
      if (currentTopIndex + scrollParameters.initialRecords < rows.length) {
        setCurrentTopIndex(
          Math.ceil(currentTopIndex + scrollParameters.loadRecords),
        );
        setEndOfResults && setEndOfResults(false);
      } else {
        setEndOfResults && setEndOfResults(true);
      }
    }
  };

  return (
    <Box
      margin={0}
      width="auto"
      overflow="auto"
      className={cx('tableWrapper', className)}
      height={height || 'fit-content'}
      ref={containerRef}
      onScroll={
        onContainerScroll
          ? onContainerScroll
          : isTableLazyLoad
          ? onContainerScrollTable
          : undefined
      }
      sx={[
        { overflowY: 'overlay' },
        ...(Array.isArray(wrapperSx) ? wrapperSx : [wrapperSx]),
      ]}
      position="relative"
    >
      {isLoading && (
        <LoadingOverlay>
          <CircularProgress />
        </LoadingOverlay>
      )}
      <StyledTable
        {...getTableProps()}
        className={cx({
          hasCollapseData,
          isOnRowClick,
        })}
        headerNotCaps={headerNotCaps}
        disableRowHover={disableRowHover}
      >
        <TableHeader
          headerGroups={headerGroups}
          isAllRowsSelected={isAllRowsSelected}
          toggleAllRowsSelected={toggleAllRowsSelected}
          withRowSelect={!!onRowsSelect && !withCustomSelect}
          saveSortBy={saveSortBy}
        />
        <TableBody {...getTableBodyProps()}>
          {rows.map((row, index) => {
            if (
              currentTopIndex + scrollParameters.initialRecords >=
              rows.length
            ) {
              setEndOfResults && setEndOfResults(true);
            }
            prepareRow(row);
            if (isTableLazyLoad) {
              if (index < currentTopIndex + scrollParameters.initialRecords) {
                return (
                  <Row<D>
                    key={index}
                    row={row}
                    index={index}
                    hasCollapseData={hasCollapseData}
                    onRowClick={onRowClick}
                    onRowDoubleClick={onRowDoubleClick}
                    onRowHover={onRowHover}
                    withRowSelect={!!onRowsSelect}
                    withCustomSelect={withCustomSelect}
                    getRowClass={getRowClass}
                  />
                );
              }
            } else {
              return (
                <Row<D>
                  key={index}
                  row={row}
                  index={index}
                  hasCollapseData={hasCollapseData}
                  onRowClick={onRowClick}
                  onRowDoubleClick={onRowDoubleClick}
                  onRowHover={onRowHover}
                  withRowSelect={!!onRowsSelect}
                  withCustomSelect={withCustomSelect}
                  getRowClass={getRowClass}
                />
              );
            }
          })}
        </TableBody>
        <TableFooter>
          {emptyTableInfo && !isLoading && !data.length && (
            <TableRow className="emptyTableInfo">
              <TableCell sx={{ fontSize: '1rem' }}>{emptyTableInfo}</TableCell>
            </TableRow>
          )}
        </TableFooter>
      </StyledTable>
      {children}
    </Box>
  );
}

const Table = forwardRef(TableInner) as <D extends object>(
  props: PropsWithChildren<TableProps<D>> & {
    ref?: React.ForwardedRef<TableRefType>;
  },
) => ReturnType<typeof TableInner>;

export default Table;
